import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Fade,
  Snackbar,
  Alert,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Description as DescriptionIcon,
  ContactPage as ContactPageIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';

// Import tab components
import PersonalTab from './employees-components/PersonalTab';
import WorkTab from './employees-components/WorkTab';
import CertificationsTab from './employees-components/CertificationsTab';
import ResumeTab from './employees-components/ResumeTab';

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
  }
}));

const EmployeeEditor = () => {
  const theme = useTheme();
  const { companyId, employeeId } = useParams();
  const navigate = useNavigate();
  const { 
    canEditPage, 
    canDeletePage, 
    canViewWorkInformation, 
    canEditWorkInformation,
    canViewResume,
    canEditResume,
    canViewCertifications,
    canEditCertifications,
    canViewPersonalDetails,
    canEditPersonalDetails,
    canViewCitizenshipIdentity,
    canEditCitizenshipIdentity,
    canDeleteEmployees
  } = usePermissions();
  
  const [employee, setEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('create'); // 'create' or 'edit'
  const [currentTab, setCurrentTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Full employee form data with all fields
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: '',
    last_name: '',
    email: '',
    employee_number: '',
    work_phone: '',
    personal_phone: '',
    
    // Personal Information
    personal_email: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    work_permit_no: '',
    work_permit_expiration: '',
    work_permit_document_path: '',
    work_permit_document_name: '',
    street_address: '',
    street_address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    certificate_level: '',
    field_of_study: '',
    legal_name: '',
    date_of_birth: '',
    gender: '',
    visa_no: '',
    visa_expiration_date: '',
    nationality: '',
    identification_no: '',
    ssn_no: '',
    passport_no: '',
    marital_status: '',
    dependent_children: 0,
    
    // Work Information
    department_id: '',
    job_position: '',
    job_title: '',
    manager_id: '',
    manager_name: '',
    work_street_address: '',
    work_street_address_2: '',
    work_city: '',
    work_state: '',
    work_zip_code: '',
    work_country: '',
    work_location_id: '',
    work_location: '',
    hire_date: '',
    employment_type: 'employee',
    contract_start_date: '',
    contract_end_date: '',
    hourly_wage: '',
    currency: 'USD',
    working_hours: '',
    timezone: 'America/New_York',
    
    // Resume
    resume_path: '',
    resume_name: '',
    resume_upload_date: null,
    
    // Additional fields
    notes: '',
    job_description: '',
    profile_image_url: ''
  });

  // Certifications and remote schedule
  const [certifications, setCertifications] = useState([]);
  const [remoteSchedule, setRemoteSchedule] = useState([
    { day_of_week: 0, work_location: '', notes: '' }, // Sunday
    { day_of_week: 1, work_location: '', notes: '' }, // Monday
    { day_of_week: 2, work_location: '', notes: '' }, // Tuesday
    { day_of_week: 3, work_location: '', notes: '' }, // Wednesday
    { day_of_week: 4, work_location: '', notes: '' }, // Thursday
    { day_of_week: 5, work_location: '', notes: '' }, // Friday
    { day_of_week: 6, work_location: '', notes: '' }  // Saturday
  ]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showAlert = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    loadData();
  }, [employeeId, companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load departments
      await loadDepartments();
      
      // Load employees for manager selection
      await loadEmployees();
      
      if (employeeId && employeeId !== 'new') {
        // Edit mode
        setMode('edit');
        await loadEmployee(employeeId);
      } else {
        // Create mode
        setMode('create');
        setEmployee(null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showAlert('Error loading data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployee = async (id) => {
    try {
      const { data, error } = await supabase
        .rpc('get_employee_decrypted', {
          employee_id_param: id,
          company_id_param: companyId
        });

      if (error) throw error;
      
      const employeeData = data?.[0];
      
      setEmployee(employeeData);
      
      // Map all the employee data to formData
      setFormData({
        // Basic Information
        first_name: employeeData.first_name || '',
        last_name: employeeData.last_name || '',
        email: employeeData.email || '',
        employee_number: employeeData.employee_number || '',
        work_phone: employeeData.work_phone || '',
        personal_phone: employeeData.personal_phone || '',
        
        // Personal Information
        personal_email: employeeData.personal_email || '',
        emergency_contact_name: employeeData.emergency_contact_name || '',
        emergency_contact_phone: employeeData.emergency_contact_phone || '',
        work_permit_no: employeeData.work_permit_no || '',
        work_permit_expiration: employeeData.work_permit_expiration || '',
        work_permit_document_path: employeeData.work_permit_document_path || '',
        work_permit_document_name: employeeData.work_permit_document_name || '',
        street_address: employeeData.street_address || '',
        street_address_2: employeeData.street_address_2 || '',
        city: employeeData.city || '',
        state: employeeData.state || '',
        zip_code: employeeData.zip_code || '',
        country: employeeData.country || '',
        certificate_level: employeeData.certificate_level || '',
        field_of_study: employeeData.field_of_study || '',
        legal_name: employeeData.legal_name || '',
        date_of_birth: employeeData.date_of_birth || '',
        gender: employeeData.gender || '',
        visa_no: employeeData.visa_no || '',
        visa_expiration_date: employeeData.visa_expiration_date || '',
        nationality: employeeData.nationality || '',
        identification_no: employeeData.identification_no || '',
        ssn_no: employeeData.ssn_no || '',
        passport_no: employeeData.passport_no || '',
        marital_status: employeeData.marital_status || '',
        dependent_children: employeeData.dependent_children || 0,
        
        // Work Information
        department_id: employeeData.department_id || '',
        job_position: employeeData.job_position || '',
        job_title: employeeData.job_title || '',
        manager_id: employeeData.manager_id || '',
        manager_name: employeeData.manager_name || '',
        work_street_address: employeeData.work_street_address || '',
        work_street_address_2: employeeData.work_street_address_2 || '',
        work_city: employeeData.work_city || '',
        work_state: employeeData.work_state || '',
        work_zip_code: employeeData.work_zip_code || '',
        work_country: employeeData.work_country || '',
        work_location_id: employeeData.work_location_id || '',
        work_location: employeeData.work_location || '',
        hire_date: employeeData.hire_date || '',
        employment_type: employeeData.employment_type || 'employee',
        contract_start_date: employeeData.contract_start_date || '',
        contract_end_date: employeeData.contract_end_date || '',
        hourly_wage: employeeData.hourly_wage || '',
        currency: employeeData.currency || 'USD',
        working_hours: employeeData.working_hours || '',
        timezone: employeeData.timezone || 'America/New_York',
        
        // Resume
        resume_path: employeeData.resume_path || '',
        resume_name: employeeData.resume_name || '',
        resume_upload_date: employeeData.resume_upload_date || null,
        
        // Additional fields
        notes: employeeData.notes || '',
        job_description: employeeData.job_description || '',
        profile_image_url: employeeData.profile_image_url || ''
      });

      // Load certifications
      await loadCertifications(id);
      
      // Load remote schedule
      await loadRemoteSchedule(id);
      
    } catch (error) {
      console.error('Error loading employee:', error);
      showAlert('Error loading employee: ' + error.message, 'error');
    }
  };

  const loadCertifications = async (employeeId) => {
    try {
      const { data, error } = await supabase
        .rpc('get_employee_certifications_decrypted', {
          employee_id_param: employeeId,
          company_id_param: companyId
        });

      if (error) throw error;
      setCertifications(data || []);
    } catch (error) {
      console.error('Error loading certifications:', error);
    }
  };

  const loadRemoteSchedule = async (employeeId) => {
    try {
      const { data, error } = await supabase
        .from('employee_remote_schedule')
        .select('*')
        .eq('employee_id', employeeId)
        .order('day_of_week');

      if (error) throw error;
      
      if (data && data.length > 0) {
        setRemoteSchedule(data);
      }
    } catch (error) {
      console.error('Error loading remote schedule:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, first_name, last_name, job_title')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('last_name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      showAlert('First name and last name are required', 'error');
      return;
    }

    // Check if user has edit permissions for the current tab
    const currentTabHasEditPermission = () => {
      let tabIndex = 0;
      
      if (canViewWorkInformation()) {
        if (currentTab === tabIndex) return canEditWorkInformation();
        tabIndex++;
      }
      
      if (canViewResume()) {
        if (currentTab === tabIndex) return canEditResume();
        tabIndex++;
      }
      
      if (canViewCertifications()) {
        if (currentTab === tabIndex) return canEditCertifications();
        tabIndex++;
      }
      
      if (canViewPersonalDetails()) {
        if (currentTab === tabIndex) return canEditPersonalDetails();
        tabIndex++;
      }
      
      return false;
    };

    if (!currentTabHasEditPermission()) {
      showAlert('You do not have permission to edit this section', 'error');
      return;
    }

    try {
      // Encrypt sensitive data using Supabase RPC function
      const encryptField = async (value) => {
        if (!value || !value.trim()) return null;
        
        const { data, error } = await supabase.rpc('encrypt_employee_field', {
          field_value: value.trim()
        });
        
        if (error) throw error;
        return data;
      };

      const saveData = {
        company_id: companyId,
        // Basic Information - only first and last name required
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email?.trim() || null,
        employee_number: formData.employee_number?.trim() || null,
        work_phone: formData.work_phone?.trim() || null,
        
        // Encrypted Personal Information
        personal_email_encrypted: await encryptField(formData.personal_email),
        personal_phone_encrypted: await encryptField(formData.personal_phone),
        emergency_contact_name_encrypted: await encryptField(formData.emergency_contact_name),
        emergency_contact_phone_encrypted: await encryptField(formData.emergency_contact_phone),
        legal_name_encrypted: await encryptField(formData.legal_name),
        date_of_birth_encrypted: await encryptField(formData.date_of_birth),
        street_address_encrypted: await encryptField(formData.street_address),
        street_address_2_encrypted: await encryptField(formData.street_address_2),
        city_encrypted: await encryptField(formData.city),
        state_encrypted: await encryptField(formData.state),
        zip_code_encrypted: await encryptField(formData.zip_code),
        country_encrypted: await encryptField(formData.country),
        work_permit_no_encrypted: await encryptField(formData.work_permit_no),
        visa_no_encrypted: await encryptField(formData.visa_no),
        nationality_encrypted: await encryptField(formData.nationality),
        identification_no_encrypted: await encryptField(formData.identification_no),
        ssn_no_encrypted: await encryptField(formData.ssn_no),
        passport_no_encrypted: await encryptField(formData.passport_no),
        
        // Non-encrypted fields
        work_permit_expiration: formData.work_permit_expiration || null,
        work_permit_document_path: formData.work_permit_document_path?.trim() || null,
        work_permit_document_name: formData.work_permit_document_name?.trim() || null,
        certificate_level: formData.certificate_level?.trim() || null,
        field_of_study: formData.field_of_study?.trim() || null,
        gender: formData.gender?.trim() || null,
        visa_expiration_date: formData.visa_expiration_date || null,
        marital_status: formData.marital_status?.trim() || null,
        dependent_children: formData.dependent_children || 0,
        
        // Work Information - all optional
        department_id: formData.department_id || null,
        job_position: formData.job_position?.trim() || null,
        job_title: formData.job_title?.trim() || null,
        manager_id: formData.manager_id || null,
        manager_name: formData.manager_name?.trim() || null,
        work_street_address: formData.work_street_address?.trim() || null,
        work_street_address_2: formData.work_street_address_2?.trim() || null,
        work_city: formData.work_city?.trim() || null,
        work_state: formData.work_state?.trim() || null,
        work_zip_code: formData.work_zip_code?.trim() || null,
        work_country: formData.work_country?.trim() || null,
        work_location_id: formData.work_location_id || null,
        work_location: formData.work_location?.trim() || null,
        hire_date: formData.hire_date || null,
        employment_type: formData.employment_type || 'employee',
        contract_start_date: formData.contract_start_date || null,
        contract_end_date: formData.contract_end_date || null,
        hourly_wage: formData.hourly_wage ? parseFloat(formData.hourly_wage) : null,
        currency: formData.currency || 'USD',
        working_hours: formData.working_hours?.trim() || null,
        timezone: formData.timezone || 'America/New_York',
        
        // Resume - handle pending uploads/deletes
        resume_path: formData.resume_path?.trim() || null,
        resume_name: formData.resume_name?.trim() || null,
        resume_upload_date: formData.resume_upload_date || null,
        
        // Additional fields - optional
        notes: formData.notes?.trim() || null,
        job_description: formData.job_description?.trim() || null,
        profile_image_url: formData.profile_image_url?.trim() || null
      };

      // Handle resume file operations before saving employee data
      if (formData.resume_pending_delete && formData.resume_path_to_delete) {
        try {
          const { error: deleteError } = await supabase.storage
            .from('employee-documents')
            .remove([formData.resume_path_to_delete]);
          
          if (deleteError) {
            console.warn('Error deleting old resume:', deleteError);
          }
        } catch (deleteErr) {
          console.warn('Failed to delete old file:', deleteErr);
        }
      }

      if (formData.resume_pending_upload && formData.resume_file_data) {
        try {
          // Generate unique file name
          const fileExt = formData.resume_name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
          const filePath = `${companyId}/resumes/${fileName}`;

          // Convert ArrayBuffer back to File
          const fileBlob = new Blob([formData.resume_file_data], { type: formData.resume_file_type });
          
          // Upload file to Supabase Storage
          const { error: uploadError } = await supabase.storage
            .from('employee-documents')
            .upload(filePath, fileBlob);

          if (uploadError) throw uploadError;

          // Update save data with new file path
          saveData.resume_path = filePath;
          
          // Delete old file if replacing
          if (formData.resume_path && formData.resume_path !== filePath) {
            try {
              const { error: deleteError } = await supabase.storage
                .from('employee-documents')
                .remove([formData.resume_path]);
              
              if (deleteError) {
                console.warn('Error deleting old resume:', deleteError);
              }
            } catch (deleteErr) {
              console.warn('Failed to delete old file:', deleteErr);
            }
          }
        } catch (error) {
          console.error('Error uploading resume:', error);
          showAlert('Error uploading resume: ' + error.message, 'error');
          return;
        }
      }

      let employeeId;
      
      if (mode === 'edit') {
        const { error } = await supabase
          .from('employees')
          .update(saveData)
          .eq('id', employee.id);

        if (error) throw error;
        employeeId = employee.id;
        
        // Clear pending flags after successful save
        if (formData.resume_pending_upload) {
          handleInputChange('resume_pending_upload', false);
          handleInputChange('resume_file_data', null);
          handleInputChange('resume_file_size', null);
          handleInputChange('resume_file_type', null);
          handleInputChange('resume_path', saveData.resume_path);
        }
        
        if (formData.resume_pending_delete) {
          handleInputChange('resume_pending_delete', false);
          handleInputChange('resume_path_to_delete', null);
        }
        
        showAlert('Employee updated successfully');
      } else {
        const { data, error } = await supabase
          .from('employees')
          .insert([saveData])
          .select()
          .single();

        if (error) throw error;
        employeeId = data.id;
        showAlert('Employee created successfully');
        
        // Navigate back to employees list after creating
        setTimeout(() => {
          navigate(`/company/${companyId}/employees`);
        }, 1000);
      }

      // Save certifications only if they exist
      if (certifications.length > 0) {
        await saveCertifications(employeeId);
      }
      
      // Save remote schedule only if it has data
      const hasScheduleData = remoteSchedule.some(day => day.work_location || day.notes);
      if (hasScheduleData) {
        await saveRemoteSchedule(employeeId);
      }

    } catch (error) {
      console.error('Error saving employee:', error);
      showAlert('Error saving employee: ' + error.message, 'error');
    }
  };

  const saveCertifications = async (employeeId) => {
    try {
      // Delete existing certifications
      await supabase
        .from('employee_certifications')
        .delete()
        .eq('employee_id', employeeId);

      // Insert new certifications
      if (certifications.length > 0) {
        const certsToSave = await Promise.all(
          certifications
            .filter(cert => cert.is_active !== false)
            .map(async (cert) => {
              const { id, notes, ...certData } = cert;
              
              // Encrypt notes if provided
              let encryptedNotes = null;
              if (notes?.trim()) {
                const { data, error } = await supabase.rpc('encrypt_employee_field', {
                  field_value: notes.trim()
                });
                
                if (error) throw error;
                encryptedNotes = data;
              }
              
              return {
                ...certData,
                employee_id: employeeId,
                company_id: companyId,
                earned_date: certData.earned_date || null,
                expiration_date: certData.expiration_date || null,
                notes_encrypted: encryptedNotes
              };
            })
        );

        if (certsToSave.length > 0) {
          const { error } = await supabase
            .from('employee_certifications')
            .insert(certsToSave);

          if (error) throw error;
        }
      }
    } catch (error) {
      console.error('Error saving certifications:', error);
    }
  };

  const saveRemoteSchedule = async (employeeId) => {
    try {
      // Delete existing schedule
      await supabase
        .from('employee_remote_schedule')
        .delete()
        .eq('employee_id', employeeId);

      // Insert new schedule
      const scheduleToSave = remoteSchedule
        .filter(day => day.work_location || day.notes)
        .map(day => ({
          employee_id: employeeId,
          company_id: companyId,
          day_of_week: day.day_of_week,
          work_location: day.work_location,
          notes: day.notes
        }));

      if (scheduleToSave.length > 0) {
        const { error } = await supabase
          .from('employee_remote_schedule')
          .insert(scheduleToSave);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error saving remote schedule:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', employee.id);

      if (error) throw error;
      
      showAlert('Employee deleted successfully');
      setDeleteDialogOpen(false);
      navigate(`/company/${companyId}/employees`);
    } catch (error) {
      console.error('Error deleting employee:', error);
      showAlert('Error deleting employee: ' + error.message, 'error');
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleCertificationsChange = (newCertifications) => {
    setCertifications(newCertifications);
  };

  const handleRemoteScheduleChange = (newSchedule) => {
    setRemoteSchedule(newSchedule);
  };

  // Tab panel component
  function TabPanel({ children, value, index, ...other }) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`employee-tabpanel-${index}`}
        aria-labelledby={`employee-tab-${index}`}
        {...other}
      >
        {value === index && children}
      </div>
    );
  }

  function a11yProps(index) {
    return {
      id: `employee-tab-${index}`,
      'aria-controls': `employee-tabpanel-${index}`,
    };
  }

  // Check if user has access to at least one tab
  const hasTabAccess = canViewWorkInformation() || canViewResume() || canViewCertifications() || canViewPersonalDetails();
  
  if (!hasTabAccess) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Employee Editor Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Employee Editor. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress 
              size={60} 
              thickness={4}
              sx={{ 
                color: theme.palette.primary.main,
                mb: 3,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            />
            <LinearProgress 
              sx={{ 
                mb: 3,
                height: 4,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.primary.main
                }
              }} 
            />
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: 'primary.main',
              mb: 1
            }}>
              Loading Employee Data...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Preparing employee information
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Container 
        maxWidth="xl" 
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >


        {/* Employee Header Card */}
        <Card sx={{ 
          borderRadius: 3,
          mb: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ 
                width: 80, 
                height: 80,
                fontSize: '2rem',
                fontWeight: 500,
                bgcolor: (() => {
                  const selectedDept = departments.find(dept => dept.id === formData.department_id);
                  return selectedDept?.color || theme.palette.grey[400];
                })(),
                color: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
              }}>
                {formData.first_name?.[0] || 'A'}{formData.last_name?.[0] || ''}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {formData.first_name || 'First Name'}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>
                  {formData.last_name || 'Last Name'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {formData.email || 'email@example.com'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formData.work_phone || 'No phone'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formData.employee_number || 'No employee number'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Paper sx={{ 
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
          mb: 3
        }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            sx={{
              px: 3,
              py: 1,
              '& .MuiTab-root': {
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.9rem',
                minHeight: 48,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 500
                }
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 1.5
              }
            }}
          >
            {canViewWorkInformation() && <Tab label="Work Information" {...a11yProps(0)} />}
            {canViewResume() && <Tab label="Resume" {...a11yProps(canViewWorkInformation() ? 1 : 0)} />}
            {canViewCertifications() && <Tab label="Certifications" {...a11yProps((canViewWorkInformation() ? 1 : 0) + (canViewResume() ? 1 : 0))} />}
            {canViewPersonalDetails() && <Tab label="Personal Details" {...a11yProps((canViewWorkInformation() ? 1 : 0) + (canViewResume() ? 1 : 0) + (canViewCertifications() ? 1 : 0))} />}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Paper sx={{ 
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          mb: 10
        }}>
        {canViewWorkInformation() && (
          <TabPanel value={currentTab} index={0}>
            <WorkTab
              formData={formData}
              onInputChange={handleInputChange}
              remoteSchedule={remoteSchedule}
              onRemoteScheduleChange={handleRemoteScheduleChange}
              departments={departments}
              employees={employees}
              companyId={companyId}
            />
          </TabPanel>
        )}

        {canViewResume() && (
          <TabPanel value={currentTab} index={canViewWorkInformation() ? 1 : 0}>
            <ResumeTab
              formData={formData}
              onInputChange={handleInputChange}
              companyId={companyId}
            />
          </TabPanel>
        )}

        {canViewCertifications() && (
          <TabPanel value={currentTab} index={(canViewWorkInformation() ? 1 : 0) + (canViewResume() ? 1 : 0)}>
            <CertificationsTab
              certifications={certifications}
              onCertificationsChange={handleCertificationsChange}
              companyId={companyId}
              employeeId={employee?.id}
            />
          </TabPanel>
        )}

        {canViewPersonalDetails() && (
          <TabPanel value={currentTab} index={(canViewWorkInformation() ? 1 : 0) + (canViewResume() ? 1 : 0) + (canViewCertifications() ? 1 : 0)}>
            <PersonalTab
              formData={formData}
              onInputChange={handleInputChange}
              companyId={companyId}
            />
          </TabPanel>
        )}
        </Paper>

        {/* Fixed Action Buttons */}
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'white',
          borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 -4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
          zIndex: 1000,
          p: 2
        }}>
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mr: -30 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/company/${companyId}/employees`)}
                sx={{ fontWeight: 500 }}
              >
                Back to Employees
              </Button>
              
              {mode === 'edit' && employee && canDeleteEmployees() && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{ fontWeight: 500 }}
                >
                  Delete
                </Button>
              )}
              
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!formData.first_name?.trim() || !formData.last_name?.trim() || !(() => {
                  let tabIndex = 0;
                  
                  if (canViewWorkInformation()) {
                    if (currentTab === tabIndex) return canEditWorkInformation();
                    tabIndex++;
                  }
                  
                  if (canViewResume()) {
                    if (currentTab === tabIndex) return canEditResume();
                    tabIndex++;
                  }
                  
                  if (canViewCertifications()) {
                    if (currentTab === tabIndex) return canEditCertifications();
                    tabIndex++;
                  }
                  
                  if (canViewPersonalDetails()) {
                    if (currentTab === tabIndex) return canEditPersonalDetails();
                    tabIndex++;
                  }
                  
                  return false;
                })()}
                sx={{ fontWeight: 500 }}
              >
                {mode === 'edit' ? 'Save Changes' : 'Create Employee'}
              </Button>
            </Box>
          </Container>
        </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        PaperProps={{
          sx: {
            position: 'fixed !important',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Delete Employee
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{formData.first_name} {formData.last_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </Fade>
  );
};

export default EmployeeEditor;
